'use client'

import { useDebounce } from '@uidotdev/usehooks'
import { createContext, useEffect, useState } from 'react'
import { defaultTheme } from '../translate/util/constants'
import { getColor } from '../translate/util/color'
import { colorIdFromTheme, langColors, type ColorIds, type ThemeId } from './lang-colors'

type ThemeContextProps = {
    colors: string[]
    theme: ThemeId
    setTheme: (theme: ThemeId) => void
    setColorId?: (colorId: ColorIds) => void
    nextColorId: () => void
}

export const ThemeContext = createContext({
    colors: langColors.og,
    theme: defaultTheme,
    setTheme: () => {},
    setColorId: () => {},
    nextColorId: () => {},
} as ThemeContextProps)

// function getNextId(currentColorId: ColorIds) {
//     const keysToPickFrom = Object.keys(langColors) as ColorIds[]
//     const index = keysToPickFrom.indexOf(currentColorId)
//     return (keysToPickFrom[index + 1] || keysToPickFrom[0]) as ColorIds
// }

type Props = {
    children: React.ReactNode
    currentTheme?: ThemeId
    saveTheme?: (theme: ThemeId) => void
    saveColorId?: (colorId: ColorIds) => void
    currentColorId?: ColorIds
}
const ThemeManager = ({ children, currentTheme: initialTheme, saveColorId, saveTheme }: Props) => {
    const [theme, setTheme] = useState<ThemeId>(initialTheme || defaultTheme)
    const initialColorId: ColorIds = colorIdFromTheme(theme)
    const [colorId, _setColorId] = useState<ColorIds>(initialColorId)
    const colors = langColors[colorId] || langColors.og
    const debouncedColorId = useDebounce(colorId, 50)
    const debouncedTheme = useDebounce(theme, 2000)
    useEffect(() => {
        if (initialTheme !== debouncedTheme) {
            saveTheme?.(debouncedTheme)
        }
    }, [debouncedTheme, initialTheme, saveTheme])
    useEffect(() => {
        if (initialColorId !== debouncedColorId) {
            saveColorId?.(debouncedColorId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedColorId])
    useEffect(() => {
        document.querySelector('html')?.setAttribute('data-theme', theme)
        const root = document.documentElement.style
        for (let i = 0; i < 12; i++) {
            const c = colors[i % colors.length]
            root.setProperty(`--skeleton-color-${i}`, c)
            root.setProperty(`--skeleton-text-color-${i}`, getColor(c))
        }
    }, [theme, colors])
    return (
        <ThemeContext
            value={{
                theme,
                setTheme: (theme: ThemeId) => {
                    _setColorId(colorIdFromTheme(theme))
                    setTheme(theme)
                    try {
                        document.cookie = `theme=${theme}; max-age=2147483647`
                    } catch (error) {
                        console.error(error)
                    }
                },
                colors,
                nextColorId: () => {},
            }}
        >
            {children}
        </ThemeContext>
    )
}
export default ThemeManager
